// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { LoadingModal } from 'components/molecules/LoadingModal';
import { Floor } from 'components/molecules/SelectFloorCard';
import { Section } from 'components/molecules/SelectSectionCard';
import { Tenant } from 'components/molecules/SelectTenantCard';
import TennantAuthenPage from 'components/organisms/TennantAuthen';
import { PrimaryTemplate } from 'components/templates/PrimaryTemplate';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { selectLoading } from 'redux/slices/loadingSlice';
import {
  isTenantInfoLoaded,
  selectSectionsWithNoTenant,
  selectSectionsWithTenants,
  selectTenantsWithNoSection,
  selectTenantsWithSections,
  setFloors,
  setForceReloadSection,
  setSections,
  setTenants,
} from 'redux/slices/siteInfoSlice';
import { nonNull } from 'util/commons';
import {
  selectIsUserSubEmissionSite,
  selectUser,
} from 'redux/slices/userSlice';
import { Path } from 'routes';
import { createAxios } from 'ts/createAxios';
import { DbManager } from 'util/DbManager';
import { RoleType, TenantAuthen } from 'util/Enums';
import { handleError } from 'util/errorHandler';
import { NETWORK_ERROR_MESSAGE } from 'util/ConstantValues';
import { getSubSiteRegistrationResources } from 'apis/operator_site/siteApi';
import { SelectTenant } from 'components/organisms/SelectTenant';
import { useQuery } from 'util/queryParams';

type FetchSectionsRes = {
  sections: Section[];
  operatorName: string;
  siteName: string;
};
export type FetchTenantsRes = {
  tenants: Tenant[];
  operatorName: string;
  siteName: string;
};
type FetchFloorsRes = {
  floors: Floor[];
  operatorName: string;
  siteName: string;
};
const SelectSection = () => {
  const dispatch = useDispatch();
  const isLoaded = useSelector(isTenantInfoLoaded);
  const [selectedSection, setSelectedSection] = React.useState<Section>();
  const [selectedFloor, setSelectedFloor] = React.useState<Floor>();
  const api = createAxios();
  const user = useSelector(selectUser);
  const isUserSubEmissionSite = useSelector(selectIsUserSubEmissionSite);
  const history = useHistory();
  const { offlineInitialized } = useSelector(selectLoading);
  const searchParams = useQuery();
  const searchParamInitialized = React.useRef(false);

  const handleSelectSection = (section?: Section) => {
    setSelectedSection(section);
    const search = new URLSearchParams(history.location.search);

    if (section) {
      search.set('sectionId', section.id.toString());
    } else {
      search.delete('sectionId');
    }

    history.replace({
      pathname: history.location.pathname,
      hash: history.location.hash,
      search: search.toString(),
    });

    if (searchParamInitialized.current) {
      handleSelectFloor(section?.floors?.[0]);
    }
  };

  const handleSelectFloor = (floor?: Floor) => {
    setSelectedFloor(floor);
    const search = new URLSearchParams(history.location.search);

    if (floor) {
      search.set('floorId', floor.id.toString());
    } else {
      search.delete('floorId');
    }

    history.replace({
      pathname: history.location.pathname,
      hash: history.location.hash,
      search: search.toString(),
    });
  };

  const fetchData = async (force = false) => {
    if (isUserSubEmissionSite) {
      await getSubSiteRegistrationResources({
        siteId: nonNull(user.siteId),
        successCallback: ({ tenants }) => {
          if (!tenants.length) {
            history.push(Path.emissionNew);
          }
        },
      });

      return;
    }

    if (user && !user.isShowTenants) {
      history.push(Path.emissionNew);
      return;
    }

    // リロードボタン押下時または、サイト情報がロードされていない場合
    if (force || !isLoaded) {
      if (!offlineInitialized) {
        return;
      }

      const fetchLocalData = async () => {
        const cachedSites = await DbManager.sites.toArray();

        if (cachedSites.length) {
          const [cachedSections, cachedTenants, cachedFloors] =
            await Promise.all([
              DbManager.sections.orderBy('name').toArray(),
              DbManager.tenants.orderBy('name').toArray(),
              DbManager.floors.orderBy('name').toArray(),
            ]);

          dispatch(setSections(cachedSections));
          dispatch(setTenants(cachedTenants));
          dispatch(setFloors(cachedFloors));

          return true;
        }

        return false;
      };

      if (!force) {
        const isLocalDataFetched = await fetchLocalData();

        if (isLocalDataFetched) {
          return;
        }
      }

      Promise.all([
        api.get<FetchSectionsRes>(`/sections?siteId=${user.siteId}`),
        api.get<FetchFloorsRes>(`/floors?siteId=${user.siteId}`),
        api.get<FetchTenantsRes>(`/tenants?siteId=${user.siteId}`),
      ])
        .then(async (response) => {
          dispatch(setSections(response[0].data.sections));
          dispatch(setTenants(response[2].data.tenants));
          dispatch(setFloors(response[1].data.floors));
          dispatch(setForceReloadSection(true));

          await Promise.all([
            DbManager.sections.clear(),
            DbManager.tenants.clear(),
            DbManager.floors.clear(),
          ]);

          await Promise.all([
            DbManager.sections.bulkAdd(response[0].data.sections),
            DbManager.tenants.bulkAdd(response[2].data.tenants),
            DbManager.floors.bulkAdd(response[1].data.floors),
          ]);
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch(async (e: any) => {
          if (e.message === NETWORK_ERROR_MESSAGE) {
            await fetchLocalData();
          } else {
            handleError(e);
          }
        });
    }
  };

  React.useEffect(() => {
    if (user && offlineInitialized) {
      fetchData();
    }
  }, [user, offlineInitialized]);

  const sectionsWithTenants = useSelector(selectSectionsWithTenants) || [];
  const sectionsWithNoTenants = useSelector(selectSectionsWithNoTenant) || [];

  React.useEffect(() => {
    if (!isLoaded || searchParamInitialized.current) {
      return;
    }

    const sections: (
      | (typeof sectionsWithTenants)[0]
      | (typeof sectionsWithNoTenants)[0]
    )[] = sectionsWithTenants.length
      ? sectionsWithTenants
      : sectionsWithNoTenants;

    const sectionIdFromParams = searchParams.get('sectionId');
    const preSelectedSection = sectionIdFromParams
      ? sections.find((s) => s.id.toString() === sectionIdFromParams)
      : sections[0];

    const floorIdFromParams = searchParams.get('floorId');
    const preSelectedFloor = floorIdFromParams
      ? preSelectedSection?.floors?.find(
          (s) => s.id.toString() === floorIdFromParams
        )
      : preSelectedSection?.floors?.[0];

    handleSelectSection(preSelectedSection);
    handleSelectFloor(preSelectedFloor);

    searchParamInitialized.current = true;
  }, [isLoaded]);

  const sections = useSelector(selectSectionsWithTenants);
  const oSections = useSelector(selectSectionsWithNoTenant);

  const tenants = useSelector(selectTenantsWithSections);
  const oTenants = useSelector(selectTenantsWithNoSection);

  if (!offlineInitialized) {
    return (
      <LoadingModal
        zIndex={100000}
        hasCash={true}
        isOpen={!offlineInitialized}
      />
    );
  }

  if (
    user.tenantAuthen &&
    user.tenantAuthen !== TenantAuthen.None &&
    !(
      user.role === RoleType.AdminSite &&
      user.tenantAuthen === TenantAuthen.ICCard
    ) &&
    !user.temporaryAdminSite
  ) {
    return (
      <PrimaryTemplate hasLogo>
        <TennantAuthenPage />
      </PrimaryTemplate>
    );
  }

  return (
    <SelectTenant
      sections={sections}
      oSections={oSections}
      tenants={tenants}
      oTenants={oTenants}
      selectedSection={selectedSection}
      onSelectSection={handleSelectSection}
      selectedFloor={selectedFloor}
      onSelectFloor={handleSelectFloor}
      onClickReload={() => {
        fetchData(true);
      }}
      onSelectTenant={(tenant) => {
        const searchParams = new URLSearchParams(history.location.search);
        searchParams.set('tenantId', tenant.id.toString());

        history.push({
          pathname: Path.emissionNew,
          search: searchParams.toString(),
        });
      }}
    />
  );
};
export default SelectSection;
